import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import _isEqual from 'lodash/isEqual';
import useFilterParams from '@lib/hooks/useFilterParams';
import useSearchParam from '@lib/hooks/useSearchParam';
import useTabParam from '@lib/hooks/useTabParam';
import { isItemsMatch } from '@lib/components/Table/utils';
import { BaseItem } from '@lib/components/Table/types';
import type { FetchIdsResponse } from '@lib/utils/getAllTableItemsIds';

export interface UseBulkSelectResult<T extends BaseItem> {
  getBulkSelectedItemsCount: (totalCount: number) => number;
  getSelectedItemsCount: (totalCount: number) => number;
  handleSelectAllTableItems: () => void;
  handleSelectOnlyOneTableItem: (item: T) => void;
  handleSelectSingleTableItem: (item: T | null) => void;
  handleSelectTableItem: (item: T) => void;
  handleUnselectAllTableItems: () => void;
  handleUnselectTableItem: (item: T) => void;
  initializeSelectedItems: (items: T[]) => void;
  isAllTableItemsSelected: boolean;
  selectedSingleTableItem: T | null;
  selectedTableItems: T[];
  unselectedTableItems: T[];
}

type ResolveAllItemIds<T extends BaseItem> = () => Promise<Array<T['id']>>;

export interface UseTableItemsBulkSelectWithAllIdsOptions<T extends BaseItem> {
  resolveAllItemIds?: ResolveAllItemIds<T>;
  fetchIds?: () => Promise<FetchIdsResponse<unknown>>;
  onResolveAllItemIdsError?: (error: unknown) => void;
}

export interface UseBulkSelectResultWithAllIds<
  T extends BaseItem,
> extends UseBulkSelectResult<T> {
  areAllItemsMaterialized: boolean;
  isResolvingAllItemIds: boolean;
  fetchIdsLoading: boolean;
}

/**
 * Manages table selection state for the shared `<Table />` component and consumers that layer bulk actions on top.
 *
 * How state is used around the table surface:
 * - `<TableHead />` feeds off `isAllTableItemsSelected` plus `selectedTableItems`/`unselectedTableItems` to decide the
 *   select-all checkbox state and triggers `handleSelectAllTableItems` / `handleUnselectAllTableItems`.
 * - `<TableBodyRow />` inspects the same lists to decide if a row checkbox is checked and wires every toggle through
 *   `handleSelectTableItem` and `handleUnselectTableItem`.
 * - Host screens and modals (e.g. tasks, assets, modal multi-selects) call `getSelectedItemsCount` to drive bulk action
 *   counters, and `getBulkSelectedItemsCount` to support operations that temporarily downgrade to a single item.
 *
 * Selection modes supported:
 * - Explicit selection: `selectedTableItems` holds a growing list as rows are checked individually.
 * - Select-all mode: flipping `isAllTableItemsSelected` assumes every row is selected and `unselectedTableItems`
 *   captures opt-outs so that the UI can show de-selected rows while keeping counts accurate.
 * - Single-item focus: `selectedSingleTableItem` is a dedicated slot used by detail panes or edit actions that require
 *   exactly one entity and forces `getBulkSelectedItemsCount` to report 1 while it is set.
 * - Auto reset: whenever filter/search/tab query params change, the selection state resets to keep the UI consistent
 *   with the current dataset.
 *
 * Matching logic uses `isItemsMatch` (primarily by `id`) so callers can provide plain objects from table datasets.
 */
export function useTableItemsBulkSelect<
  T extends BaseItem,
>(): UseBulkSelectResult<T> {
  const [filterParams] = useFilterParams();
  const [searchQuery] = useSearchParam();
  const [tab] = useTabParam();
  // Seeded with the params seen on mount, so the reset effect below only
  // fires on a real change — comparing against `undefined` on the first run
  // would wipe a selection seeded during render (e.g. `TableMultiselect`
  // mounting already open with preselected ids).
  const lastQueryRef = useRef({ filterParams, searchQuery, tab });
  const [selectedSingleTableItem, setSelectedSingleTableItem] =
    useState<T | null>(null);
  const [selectedTableItems, setSelectedTableItems] = useState<T[]>([]);
  const [unselectedTableItems, setUnselectedTableItems] = useState<T[]>([]);
  const [isAllTableItemsSelected, setIsAllTableItemsSelected] =
    useState<boolean>(false);

  const resetItemsState = useCallback(() => {
    setSelectedTableItems([]);
    setUnselectedTableItems([]);
    setSelectedSingleTableItem(null);
  }, []);

  const handleSelectAllTableItems = useCallback(() => {
    setIsAllTableItemsSelected(true);
    resetItemsState();
  }, [resetItemsState]);

  const handleUnselectAllTableItems = useCallback(() => {
    setIsAllTableItemsSelected(false);
    resetItemsState();
  }, [resetItemsState]);

  const handleSelectSingleTableItem = useCallback((item: T | null) => {
    setSelectedSingleTableItem(item);
  }, []);

  const handleSelectTableItem = (item: T) => {
    if (isAllTableItemsSelected) {
      setUnselectedTableItems(
        unselectedTableItems.filter(
          (unselectedItem) => !isItemsMatch(unselectedItem, item),
        ),
      );
    } else {
      setSelectedTableItems([...selectedTableItems, item]);
    }
  };

  const handleSelectOnlyOneTableItem = (item: T) => {
    setSelectedTableItems([item]);
  };

  const initializeSelectedItems = useCallback((items: T[]) => {
    setSelectedTableItems(items);
    setIsAllTableItemsSelected(false);
    setUnselectedTableItems([]);
    setSelectedSingleTableItem(null);
  }, []);

  const handleUnselectTableItem = (item: T) => {
    if (isAllTableItemsSelected) {
      setUnselectedTableItems([...unselectedTableItems, item]);
    } else {
      setSelectedTableItems(
        selectedTableItems.filter(
          (selectedItem) => !isItemsMatch(selectedItem, item),
        ),
      );
    }
  };

  const getSelectedItemsCount = (totalCount: number) => {
    return isAllTableItemsSelected && totalCount && totalCount > 0
      ? totalCount - unselectedTableItems.length
      : selectedTableItems.length;
  };

  const getBulkSelectedItemsCount = (totalCount: number) => {
    if (selectedSingleTableItem) return 1;
    return getSelectedItemsCount(totalCount);
  };

  useEffect(() => {
    const last = lastQueryRef.current;
    if (
      last.searchQuery !== searchQuery ||
      last.tab !== tab ||
      !_isEqual(filterParams, last.filterParams)
    ) {
      lastQueryRef.current = { filterParams, searchQuery, tab };
      handleUnselectAllTableItems();
    }
  }, [filterParams, handleUnselectAllTableItems, searchQuery, tab]);

  return {
    getBulkSelectedItemsCount,
    getSelectedItemsCount,
    handleSelectAllTableItems,
    handleSelectOnlyOneTableItem,
    handleSelectSingleTableItem,
    handleSelectTableItem,
    handleUnselectAllTableItems,
    handleUnselectTableItem,
    initializeSelectedItems,
    isAllTableItemsSelected,
    selectedSingleTableItem,
    selectedTableItems,
    unselectedTableItems,
  };
}

function createItemsFromIds<T extends BaseItem>(ids: Array<T['id']>): T[] {
  return ids.map((id) => ({ id }) as T);
}

/**
 * Extends bulk selection to materialize the full list of selected ids by calling `resolveAllItemIds`.
 *
 * When the resolver (or `fetchIds`) is provided, selecting all rows triggers the async resolver and stores returned
 * ids as explicit selections. This keeps downstream APIs (`Table`, `tableItemsBulkAction`, etc.) working with a fully
 * enumerated list, while still exposing the original bulk-select API surface. The hook also exposes `fetchIdsLoading`
 * so consumers can reflect the network state when `fetchIds` is used under the hood.
 */
export function useTableItemsBulkSelectWithAllIds<T extends BaseItem>(
  options: UseTableItemsBulkSelectWithAllIdsOptions<T> = {},
): UseBulkSelectResultWithAllIds<T> {
  const baseResult = useTableItemsBulkSelect<T>();
  const {
    handleSelectAllTableItems: baseHandleSelectAllTableItems,
    handleUnselectAllTableItems: baseHandleUnselectAllTableItems,
    handleSelectTableItem: baseHandleSelectTableItem,
    handleUnselectTableItem: baseHandleUnselectTableItem,
    handleSelectOnlyOneTableItem: baseHandleSelectOnlyOneTableItem,
    handleSelectSingleTableItem: baseHandleSelectSingleTableItem,
    initializeSelectedItems: baseInitializeSelectedItems,
    ...rest
  } = baseResult;

  const {
    resolveAllItemIds: providedResolveAllItemIds,
    fetchIds,
    onResolveAllItemIdsError,
  } = options;

  const hasResolveAllItemIds = Boolean(providedResolveAllItemIds || fetchIds);

  const [areAllItemsMaterialized, setAreAllItemsMaterialized] = useState(false);
  const [isResolvingAllItemIds, setIsResolvingAllItemIds] = useState(false);
  const [fetchIdsLoading, setFetchIdsLoading] = useState(false);

  const getAllItemIds = useCallback(async (): Promise<Array<T['id']>> => {
    if (providedResolveAllItemIds) {
      return providedResolveAllItemIds();
    }

    if (fetchIds) {
      setFetchIdsLoading(true);
      try {
        const response = await fetchIds();
        const collection = (response?.data?.data?.collection ?? []) as Array<{
          id: T['id'];
        }>;
        return collection.map((item) => item.id);
      } finally {
        setFetchIdsLoading(false);
      }
    }

    return [];
  }, [providedResolveAllItemIds, fetchIds]);

  const handleSelectAllTableItems = useCallback(() => {
    if (!hasResolveAllItemIds) {
      setAreAllItemsMaterialized(false);
      baseHandleSelectAllTableItems();
      return () => undefined;
    }

    setIsResolvingAllItemIds(true);
    setAreAllItemsMaterialized(false);

    return getAllItemIds()
      .then((ids) => {
        const uniqueIds = Array.from(new Set(ids ?? []));
        const items = createItemsFromIds<T>(uniqueIds);
        baseInitializeSelectedItems(items);
        setAreAllItemsMaterialized(true);
      })
      .catch((error) => {
        setAreAllItemsMaterialized(false);
        onResolveAllItemIdsError?.(error);
        baseHandleUnselectAllTableItems();
      })
      .finally(() => {
        setIsResolvingAllItemIds(false);
      });
  }, [
    hasResolveAllItemIds,
    getAllItemIds,
    onResolveAllItemIdsError,
    baseHandleSelectAllTableItems,
    baseInitializeSelectedItems,
    baseHandleUnselectAllTableItems,
  ]);

  const handleUnselectAllTableItems = useCallback(() => {
    setAreAllItemsMaterialized(false);
    baseHandleUnselectAllTableItems();
  }, [baseHandleUnselectAllTableItems]);

  const handleSelectTableItem = useCallback(
    (item: T) => {
      setAreAllItemsMaterialized(false);
      baseHandleSelectTableItem(item);
    },
    [baseHandleSelectTableItem],
  );

  const handleUnselectTableItem = useCallback(
    (item: T) => {
      setAreAllItemsMaterialized(false);
      baseHandleUnselectTableItem(item);
    },
    [baseHandleUnselectTableItem],
  );

  const handleSelectOnlyOneTableItem = useCallback(
    (item: T) => {
      setAreAllItemsMaterialized(false);
      baseHandleSelectOnlyOneTableItem(item);
    },
    [baseHandleSelectOnlyOneTableItem],
  );

  const handleSelectSingleTableItem = useCallback(
    (item: T | null) => {
      setAreAllItemsMaterialized(false);
      baseHandleSelectSingleTableItem(item);
    },
    [baseHandleSelectSingleTableItem],
  );

  const initializeSelectedItems = useCallback(
    (items: T[]) => {
      setAreAllItemsMaterialized(false);
      baseInitializeSelectedItems(items);
    },
    [baseInitializeSelectedItems],
  );

  return useMemo(
    () => ({
      ...rest,
      handleSelectAllTableItems,
      handleUnselectAllTableItems,
      handleSelectTableItem,
      handleUnselectTableItem,
      handleSelectOnlyOneTableItem,
      handleSelectSingleTableItem,
      initializeSelectedItems,
      areAllItemsMaterialized,
      isResolvingAllItemIds,
      fetchIdsLoading,
    }),
    [
      rest,
      handleSelectAllTableItems,
      handleUnselectAllTableItems,
      handleSelectTableItem,
      handleUnselectTableItem,
      handleSelectOnlyOneTableItem,
      handleSelectSingleTableItem,
      initializeSelectedItems,
      areAllItemsMaterialized,
      isResolvingAllItemIds,
      fetchIdsLoading,
    ],
  );
}
